/**
 * The backlog's structured filter, in the shape the API takes (a Cloudscape PropertyFilter query
 * minus its client-only token fields). Pure helpers, shared by the hook that sends it, the URL
 * that carries it and the sidebar presets that link to it.
 */

/** The subset of a PropertyFilter query the backlog endpoint understands. */
export interface GroupsFilterQuery {
  operation: "and" | "or";
  tokens: Array<{
    propertyKey?: string;
    operator: string;
    value: unknown;
  }>;
}

/** Wire form of the structured filter; undefined when it has no tokens so the cache key stays stable. */
export const serializeGroupsFilter = (
  filter?: GroupsFilterQuery
): string | undefined =>
  filter && filter.tokens.length
    ? JSON.stringify({
        operation: filter.operation,
        tokens: filter.tokens.map((t) => ({
          propertyKey: t.propertyKey,
          operator: t.operator,
          value: t.value,
        })),
      })
    : undefined;

/**
 * Inverse of serializeGroupsFilter, for a filter carried in a URL. Anything that is not a
 * well-formed query (missing, malformed JSON, no tokens) is undefined — the caller falls back to
 * an unfiltered view rather than failing.
 */
export function parseGroupsFilter(
  text?: string | null
): GroupsFilterQuery | undefined {
  if (!text) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== "object") return undefined;
  const { operation, tokens } = parsed as {
    operation?: unknown;
    tokens?: unknown;
  };
  if (!Array.isArray(tokens) || !tokens.length) return undefined;
  const clean = tokens.filter(
    (t): t is GroupsFilterQuery["tokens"][number] =>
      !!t &&
      typeof t === "object" &&
      typeof (t as { operator?: unknown }).operator === "string"
  );
  if (!clean.length) return undefined;
  return {
    operation: operation === "or" ? "or" : "and",
    tokens: clean.map((t) => ({
      propertyKey:
        typeof t.propertyKey === "string" ? t.propertyKey : undefined,
      operator: t.operator,
      value: t.value,
    })),
  };
}
